import type {
  ApolloClient,
  ApolloQueryResult,
  DefaultContext,
  FetchResult,
  MutationOptions,
  OperationVariables,
  QueryOptions,
} from '@apollo/client/core'
import { logError } from '~/utils/log-error'
import type { TCommon } from '~/types/app.type'

export interface ApolloClientProxyOptions {
  maxNumberOfRetries?: number
}

export type HttpClientProxyOptions = ApolloClientProxyOptions

class ApolloClientProxy {
  apolloClient: ApolloClient<TCommon>
  tokenPlugin: TCommon
  wordpressClient: ApolloClient<TCommon>

  currentRetries = 0
  maxNumberOfRetries = 0
  INTERNAL_SERVER_ERROR = 'Internal server error'

  constructor(
    apolloClient: ApolloClient<TCommon>,
    $tokenPlugin: TCommon,
    options: ApolloClientProxyOptions = {},
    wordpressClient = null,
  ) {
    this.apolloClient = apolloClient
    this.maxNumberOfRetries = options.maxNumberOfRetries || 0
    this.tokenPlugin = $tokenPlugin
    this.wordpressClient = wordpressClient
  }

  async query<T = any, TVariables = OperationVariables>(
    options: QueryOptions<TVariables, T>,
  ): Promise<ApolloQueryResult<T>> {
    try {
      const result = await this.apolloClient.query(options)
      const errors = result.errors || []

      if (
        errors[0]?.message === this.INTERNAL_SERVER_ERROR &&
        this.tokenPlugin.refreshTokenExists() &&
        this.currentRetries < this.maxNumberOfRetries
      ) {
        this.currentRetries++
        await this.tokenPlugin.refreshTokenRequest()
        return this.query(options)
      }

      if (
        errors.length > 0 &&
        this.wordpressClient &&
        this.currentRetries < this.maxNumberOfRetries
      ) {
        this.currentRetries++
        const wpResult = await this.wordpressClient.query(options)
        // Check if the WordPress result also has errors
        if (wpResult.errors && wpResult.errors.length > 0) {
          throw new Error(wpResult.errors[0]?.message || 'GraphQL Error')
        }
        return wpResult
      }

      if (errors.length > 0) {
        throw new Error(errors[0]?.message || 'GraphQL Error')
      }

      return result
    } catch (error) {
      logError(error)
      if (
        this.wordpressClient &&
        this.currentRetries < this.maxNumberOfRetries
      ) {
        this.currentRetries++
        const wpResult = await this.wordpressClient.query(options)
        // Check if the WordPress result has errors
        if (wpResult.errors && wpResult.errors.length > 0) {
          throw new Error(wpResult.errors[0]?.message || 'GraphQL Error')
        }
        return wpResult
      }
      throw error
    } finally {
      this.currentRetries = 0
    }
  }

  mutate<
    TData = any,
    TVariables = OperationVariables,
    TContext = DefaultContext,
  >(
    options: MutationOptions<TData, TVariables, TContext>,
  ): Promise<FetchResult<TData>> {
    return this.apolloClient.mutate(options)
  }
}

class HttpClientProxy {
  fetcher: TCommon
  maxNumberOfRetries = 0
  currentRetries = 0
  EXPIRED_TOKEN_MESSAGE = 'Expired token'
  tokenPlugin: TCommon

  constructor(
    $fetcher: TCommon,
    $tokenPlugin: TCommon,
    options: HttpClientProxyOptions = {},
  ) {
    this.fetcher = $fetcher
    this.maxNumberOfRetries = options.maxNumberOfRetries || 0
    this.tokenPlugin = $tokenPlugin
  }

  async request<TType>(
    url: string,
    method: string,
    body: object | FormData,
    params: object,
    extraHeaders: object = {},
  ): Promise<TType> {
    const headers = await this.tokenPlugin.getAuthorizationHeader()
    const options = {
      headers: { ...headers.headers, ...extraHeaders, 'X-Source': 'Source' },
      method,
      body,
      params,
    }
    try {
      return await this.fetcher(url, options)
    } catch (error) {
      if (
        error?.response?._data?.message === this.EXPIRED_TOKEN_MESSAGE &&
        this.tokenPlugin.refreshTokenExists() &&
        this.currentRetries < this.maxNumberOfRetries
      ) {
        this.currentRetries++
        await this.tokenPlugin.refreshTokenRequest()
        return this.request(url, method, body, params)
      }

      throw error
    }
  }
}

export { ApolloClientProxy, HttpClientProxy }
